import { ComponentType, ReactElement } from 'react';
import { Pressable, StyleSheet, Text, View } from 'react-native';

import { Fonts, Palette, Radius } from '@/constants/theme';

/**
 * Always-visible icon-tile picker. Replaces the tap-to-open dropdown
 * style (PickField) when the option set is small enough to lay flat —
 * structure (basement, foundation, roof, exterior, garage) and utilities
 * (water source, sewer, heating fuel, secondary HVAC) both fit. Users
 * see every option without a modal hop and can browse what's available
 * before picking.
 *
 * Two modes:
 *   - single: tap to select, tap again to clear
 *   - multi:  tap to toggle; the underlying value is the array of keys
 *
 * Layout fills as many tiles per row as fit (4 on a wide column, 3 on
 * a tighter column, 2 on the narrowest phones) via a fixed minWidth and
 * flexWrap. Tiles all share the same height so a tall label row doesn't
 * jostle the rest.
 */
export interface IconTileOption<V extends string | null> {
  v: V;
  label: string;
  Icon: ComponentType<{
    size?: number;
    color?: string;
    strokeWidth?: number;
  }>;
}

interface BaseProps<V extends string | null> {
  label: string;
  options: IconTileOption<V>[];
}

interface SingleProps<V extends string | null> extends BaseProps<V> {
  mode?: 'single';
  value: V | null;
  onChange: (v: V | null) => void;
}

interface MultiProps<V extends string> extends BaseProps<V> {
  mode: 'multi';
  value: V[];
  onChange: (v: V[]) => void;
}

export function IconTilePicker<V extends string | null>(props: SingleProps<V>): ReactElement;
export function IconTilePicker<V extends string>(props: MultiProps<V>): ReactElement;
export function IconTilePicker<V extends string | null>(
  props: SingleProps<V> | MultiProps<Exclude<V, null>>
): ReactElement {
  const isMulti = props.mode === 'multi';

  const isSelected = (opt: IconTileOption<V>): boolean => {
    if (isMulti) {
      return (props.value as string[]).includes(opt.v as string);
    }
    return (props as SingleProps<V>).value === opt.v;
  };

  const onPick = (opt: IconTileOption<V>): void => {
    if (isMulti) {
      const multi = props as MultiProps<Exclude<V, null>>;
      const k = opt.v as Exclude<V, null>;
      const cur = multi.value;
      multi.onChange(cur.includes(k) ? cur.filter((x) => x !== k) : [...cur, k]);
      return;
    }
    const single = props as SingleProps<V>;
    single.onChange(single.value === opt.v ? null : opt.v);
  };

  return (
    <View style={styles.wrap}>
      <Text style={styles.label}>{props.label.toUpperCase()}</Text>
      <View style={styles.row}>
        {props.options.map((opt) => {
          const sel = isSelected(opt);
          return (
            <Pressable
              key={String(opt.v ?? '__null')}
              onPress={() => onPick(opt)}
              accessibilityRole="button"
              accessibilityState={{ selected: sel }}
              style={({ pressed }) => [
                styles.tile,
                sel && styles.tileSel,
                pressed && { opacity: 0.85 },
              ]}>
              <View style={[styles.iconBubble, sel && styles.iconBubbleSel]}>
                <opt.Icon
                  size={18}
                  color={sel ? Palette.cream : Palette.forest}
                  strokeWidth={1.7}
                />
              </View>
              <Text
                style={[styles.tileLabel, sel && styles.tileLabelSel]}
                numberOfLines={2}>
                {opt.label}
              </Text>
            </Pressable>
          );
        })}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  wrap: {
    marginTop: 14,
  },
  label: {
    fontFamily: Fonts.sansBold,
    fontSize: 12,
    color: Palette.inkFaint,
    letterSpacing: 0.7,
    marginBottom: 8,
  },
  row: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  tile: {
    // Fixed ~33% width for a 3-column grid so 4/5/6/9-option lists all
    // wrap into evenly-balanced rows (3+1, 3+2, 3+3, 3+3+3). The
    // earlier flex-grow + minWidth-96 approach packed rows tightly
    // (7 in row 1, 2 orphaned in row 2 for the 9-option exterior) —
    // user called it visibly broken. flexBasis 31% with maxWidth 32%
    // caps each tile so the wrap math is predictable. flexGrow lets
    // the last row's tiles stretch to fill if there are 1-2 leftovers.
    flexBasis: '31%',
    flexGrow: 1,
    maxWidth: '32%',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingVertical: 11,
    paddingHorizontal: 11,
    borderRadius: Radius.lg,
    borderWidth: 1.5,
    borderColor: Palette.line,
    backgroundColor: Palette.card,
  },
  tileSel: {
    borderColor: Palette.forest,
    backgroundColor: '#EEF3EE',
  },
  iconBubble: {
    width: 30,
    height: 30,
    borderRadius: 9,
    backgroundColor: '#EAF0EA',
    alignItems: 'center',
    justifyContent: 'center',
  },
  iconBubbleSel: {
    backgroundColor: Palette.forest,
  },
  tileLabel: {
    flex: 1,
    fontFamily: Fonts.sansSemibold,
    fontSize: 13.5,
    color: Palette.ink,
    lineHeight: 16,
  },
  tileLabelSel: {
    color: Palette.forest,
    fontFamily: Fonts.sansBold,
  },
});
